import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import type { CheckStatus, CheckSummaryDto } from '@ops-monitor/shared';

import { monitoring } from '../api/client';
import { useQueryParams } from '../lib/query-params';
import { CheckDrawer } from '../components/CheckDrawer';
import { CheckTile } from '../components/CheckTile';
import { StatusDot } from '../components/StatusDot';
import { ErrorState, Spinner } from '../components/ui';

const STATUS_ORDER: CheckStatus[] = ['crit', 'warn', 'nodata', 'ok'];
const STATUS_LABEL: Record<CheckStatus, string> = { ok: 'норма', warn: 'внимание', crit: 'проблема', nodata: 'нет данных' };

/** Page header per app (mirrors APP_META on the api). */
const APP_TITLES: Record<string, { title: string; host: string; namespace: string }> = {
  ismart: { title: 'Моя школа', host: 'edu.ismart.org', namespace: 'prod' },
  future: { title: 'КБ (Код будущего)', host: 'edu.future.ismart.org', namespace: 'future' },
  notifications: { title: 'Почта (notifications-service)', host: 'notifications.ismart.org', namespace: 'tools' },
  ghrunners: { title: 'CI-раннеры (GitHub Actions)', host: 'gh-runner.ismart.org', namespace: 'vds2' },
  hosts: { title: 'Хосты', host: '5.35.104.39 (KZ ingress)', namespace: 'tools' },
  billing: { title: 'Балансы', host: '6 облаков', namespace: '—' },
};

/**
 * All checks of one platform, grouped by dashboard section. Toolbar filters
 * (status, view) live in the query string; `?check=<id>` deep-links straight
 * into a drawer (the Slack alerts link here).
 */
export function AppPage() {
  const appKey = useParams<{ app: string }>().app ?? '';
  const { params, setParams } = useQueryParams();
  const statusFilter = (params.status as CheckStatus | undefined) ?? null;
  const view = params.view === 'table' ? 'table' : 'tiles';

  const sections = useQuery({ queryKey: ['sections'], queryFn: ({ signal }) => monitoring.sections(signal), staleTime: Infinity });
  const checks = useQuery({
    queryKey: ['checks', appKey],
    queryFn: ({ signal }) => monitoring.checks(appKey, signal),
    refetchInterval: 30_000,
  });
  // Drawer target may arrive via ?check= (Slack link) before the user clicks.
  const [clicked, setClicked] = useState<CheckSummaryDto | null>(null);
  const deepLink = checks.data?.items.find((c) => c.id === params.check) ?? null;
  const open = clicked?.id === deepLink?.id ? clicked : deepLink;

  const counts = useMemo(() => {
    const items = checks.data?.items ?? [];
    const result: Record<CheckStatus, number> = { ok: 0, warn: 0, crit: 0, nodata: 0 };
    for (const check of items) result[check.status] += 1;
    return result;
  }, [checks.data]);

  const grouped = useMemo(() => {
    const items = (checks.data?.items ?? []).filter((check) => !statusFilter || check.status === statusFilter);
    const bySection = new Map<string, CheckSummaryDto[]>();
    for (const check of items) {
      const list = bySection.get(check.section) ?? [];
      list.push(check);
      bySection.set(check.section, list);
    }
    return bySection;
  }, [checks.data, statusFilter]);

  const openCheck = (check: CheckSummaryDto): void => {
    setClicked(check);
    setParams({ check: check.id });
  };
  const closeDrawer = (): void => {
    setClicked(null);
    setParams({ check: null });
  };

  if (checks.isPending || sections.isPending) return <Spinner label="Загружаю проверки" />;
  if (checks.isError) return <ErrorState message="Не удалось загрузить проверки" />;

  const all = checks.data.items;
  const relevant = (sections.data?.items ?? []).filter((section) => section.apps.includes(appKey) && grouped.has(section.key));

  return (
    <>
      <h1 className="page__title">{APP_TITLES[appKey]?.title ?? appKey}</h1>
      <p className="page__subtitle">
        {APP_TITLES[appKey]?.host ?? '—'} · ns {APP_TITLES[appKey]?.namespace ?? '—'} ·{' '}
        {all.length} проверок
      </p>

      <div className="toolbar">
        <div className="toolbar__group" role="group" aria-label="Фильтр по статусу">
          <button
            type="button"
            className={`chip${statusFilter === null ? ' chip--active' : ''}`}
            onClick={() => setParams({ status: null })}
          >
            все <span className="chip__count">{all.length}</span>
          </button>
          {STATUS_ORDER.map((status) => (
            <button
              key={status}
              type="button"
              className={`chip chip--${status}${statusFilter === status ? ' chip--active' : ''}`}
              onClick={() => setParams({ status: statusFilter === status ? null : status })}
              title={`Показать только «${STATUS_LABEL[status]}»`}
            >
              {STATUS_LABEL[status]} <span className="chip__count">{counts[status]}</span>
            </button>
          ))}
        </div>
        <div className="toolbar__spacer" />
        <div className="toolbar__group" role="group" aria-label="Вид">
          <button
            type="button"
            className={`chip${view === 'tiles' ? ' chip--active' : ''}`}
            onClick={() => setParams({ view: null })}
          >
            Плитки
          </button>
          <button
            type="button"
            className={`chip${view === 'table' ? ' chip--active' : ''}`}
            onClick={() => setParams({ view: 'table' })}
          >
            Таблица
          </button>
        </div>
      </div>

      {view === 'table' ? (
        <CheckTable checks={grouped.size === 0 ? [] : [...grouped.values()].flat()} onOpen={openCheck} />
      ) : (
        relevant.map((section) => (
          <section key={section.key} className="section-block" aria-label={section.title}>
            <h2 className="section-block__title">{section.title}</h2>
            <p className="section-block__desc">{section.description}</p>
            <div className="checks-grid">
              {(grouped.get(section.key) ?? []).map((check) => (
                <CheckTile key={check.id} check={check} onOpen={openCheck} />
              ))}
            </div>
          </section>
        ))
      )}
      {open ? <CheckDrawer check={open} onClose={closeDrawer} /> : null}
    </>
  );
}

/** Flat table view of every filtered check — same data, denser reading. */
function CheckTable({ checks, onOpen }: { checks: CheckSummaryDto[]; onOpen: (check: CheckSummaryDto) => void }) {
  if (checks.length === 0) return <p className="page__subtitle">под фильтр ничего не попало</p>;
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Проверка</th>
          <th>Статус</th>
          <th>Значение</th>
          <th>Обновлено</th>
          <th>Интервал</th>
        </tr>
      </thead>
      <tbody>
        {checks.map((check) => (
          <tr key={check.id} onClick={() => onOpen(check)}>
            <td>{check.title}</td>
            <td>
              <StatusDot status={check.status} />
            </td>
            <td>{check.text}</td>
            <td>{check.ranAt ? new Date(check.ranAt).toLocaleTimeString('ru-RU') : '—'}</td>
            <td>{Math.round(check.intervalSec / 60)} мин</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
